import React, { useEffect, useState, useCallback } from "react";
import { formatDate } from "@/lib/formatDate";
import { apiClient } from "@/lib/apiClient";
import { DocumentViewer } from "@/components/inbox/DocumentViewer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Sparkles,
  RefreshCw,
  Check,
  ArrowRightLeft,
  Receipt,
  FileText,
  FileInput,
  TrendingDown,
  TrendingUp,
  Loader2,
  Info,
  Banknote,
  Zap,
  ZoomIn,
  CreditCard,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CAND_TYPE = {
  receipt: { label: "Beleg", icon: Receipt, tone: "bg-amber-100 text-amber-800" },
  invoice: { label: "Kundenrechnung", icon: FileText, tone: "bg-sky-100 text-sky-800" },
  bill: { label: "Eingangsrechnung", icon: FileInput, tone: "bg-violet-100 text-violet-800" },
};

function fmtCHF(v) {
  const n = Number(v || 0);
  return n.toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function ScorePill({ score }) {
  const tone =
    score >= 95 ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
    score >= 80 ? "bg-sky-100 text-sky-800 border-sky-200" :
    "bg-amber-100 text-amber-800 border-amber-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full border ${tone} tabular-nums`}>
      {Math.round(score)}%
    </span>
  );
}

export default function BankMatchSuggestionsCard({ companyId, onMatched }) {
  const [mode, setMode] = useState("manual");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);
  const [bulkBooking, setBulkBooking] = useState(false);
  const [autoBookFxDiff, setAutoBookFxDiff] = useState(true);
  const [preview, setPreview] = useState(null); // {url, mime, label}
  const [previewLoading, setPreviewLoading] = useState(null);

  const openReceiptPreview = async (candidateId, label, kind = "receipt", attachments = [], attachmentId = null) => {
    setPreviewLoading(candidateId);
    try {
      let path = kind === "bill" ? `/bills/${candidateId}/file` : `/receipts/${candidateId}/file`;
      if (attachmentId) {
        path = kind === "bill" ? `/bills/${candidateId}/file?attachment=${attachmentId}` : `/receipts/${candidateId}/attachments/${attachmentId}/file`;
      }
      const r = await apiClient.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      setPreview((prev) => {
        if (prev?.url) URL.revokeObjectURL(prev.url);
        return { url, mime: r.data.type || "", label, candidateId, kind, attachments, activeId: attachmentId };
      });
    } catch (e) {
      toast.error(e.response?.status === 404 ? "Keine Datei zu diesem Dokument hinterlegt" : "Dokument konnte nicht geladen werden");
    } finally {
      setPreviewLoading(null);
    }
  };

  const reload = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [s, sug] = await Promise.all([
        apiClient.get(`/companies/${companyId}/bank/match-settings`),
        apiClient.get(`/companies/${companyId}/bank/match-suggestions?min_score=60`),
      ]);
      setMode(s.data?.mode || "manual");
      setSuggestions(sug.data || []);
    } catch (e) {
      console.error(e);
      toast.error("Match-Vorschläge konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const updateMode = async (newMode) => {
    try {
      await apiClient.put(`/companies/${companyId}/bank/match-settings`, { mode: newMode });
      setMode(newMode);
      toast.success(
        newMode === "auto_high"
          ? "Auto-Buchen aktiviert (≥95% wird automatisch verbucht)"
          : "Nur Vorschläge — manuelle Bestätigung nötig"
      );
    } catch (e) {
      console.error(e);
      toast.error("Modus konnte nicht gespeichert werden");
    }
  };

  const [recheckingId, setRecheckingId] = useState(null);
  const recheckReceipt = async (s) => {
    setRecheckingId(s.candidate_id);
    try {
      const r = await apiClient.post(`/receipts/${s.candidate_id}/reanalyze`);
      const amt = r.data?.amount;
      toast.success(amt != null ? `Beleg neu geprüft – Betrag jetzt CHF ${fmtCHF(amt)}` : "Beleg neu geprüft");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Neu prüfen fehlgeschlagen");
    } finally {
      setRecheckingId(null);
    }
  };

  const confirmMatch = async (s, alt) => {
    const cand = alt || s;
    setConfirmingId(alt ? `${s.journal_entry_id}:${cand.candidate_id}` : s.journal_entry_id);
    try {
      const res = await apiClient.post(`/companies/${companyId}/bank/confirm-match`, {
        journal_entry_id: s.journal_entry_id,
        candidate_type: cand.candidate_type,
        candidate_id: cand.candidate_id,
        book_fx_diff: autoBookFxDiff,
      });
      const fx = res.data?.fx_diff_booked;
      let msg = `Verknüpft: ${CAND_TYPE[cand.candidate_type]?.label || cand.candidate_type}`;
      if (fx) {
        const acc = fx.account;
        const kind = fx.kind === "loss" ? "Kursverlust" : "Kursgewinn";
        msg += ` · ${kind} CHF ${fmtCHF(fx.amount)} auf ${acc}`;
      }
      toast.success(msg);
      await reload();
      onMatched && onMatched();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Bestätigung fehlgeschlagen");
    } finally {
      setConfirmingId(null);
    }
  };

  const safeCount = suggestions.filter((s) => s.auto_bookable).length;

  const confirmAllSafe = async () => {
    setBulkBooking(true);
    try {
      const res = await apiClient.post(`/companies/${companyId}/bank/confirm-all-safe`, {
        book_fx_diff: autoBookFxDiff,
        min_score: 95,
      });
      const { booked, skipped } = res.data || {};
      if (booked > 0) {
        toast.success(
          `${booked} sichere Treffer gebucht ✓` + (skipped > 0 ? ` · ${skipped} übersprungen` : ""),
        );
      } else {
        toast.info("Keine sicheren Treffer zum Buchen gefunden.");
      }
      await reload();
      onMatched && onMatched();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Bulk-Buchung fehlgeschlagen");
    } finally {
      setBulkBooking(false);
    }
  };

  return (
    <Card className="p-4 border-slate-200" data-testid="bank-match-suggestions-card">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-md bg-violet-100 flex items-center justify-center flex-shrink-0">
            <Sparkles className="h-5 w-5 text-violet-700" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-900">Auto-Match Vorschläge</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              KI vergleicht Bank-Buchungen mit Belegen/Rechnungen — inkl. FX-Toleranz für Fremdwährungen.
            </p>
          </div>
        </div>

        {/* Mode-Toggle */}
        <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
          <div className="text-right">
            <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">Modus</div>
            <div className="text-xs font-semibold text-slate-900">
              {mode === "auto_high" ? "Auto-Buchen ≥95%" : "Nur Vorschläge"}
            </div>
          </div>
          <Switch
            checked={mode === "auto_high"}
            onCheckedChange={(checked) => updateMode(checked ? "auto_high" : "manual")}
            data-testid="bank-match-mode-toggle"
            title="Ein = sehr sichere Treffer (≥95%) werden automatisch verbucht · Aus = jeder Vorschlag muss manuell bestätigt werden"
          />
        </div>

        <div className="flex items-center gap-2">
          {safeCount > 0 && (
            <Button
              size="sm"
              onClick={confirmAllSafe}
              disabled={bulkBooking || loading}
              title="Bestätigt alle Vorschläge mit ≥95% Sicherheit auf einen Klick"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="bank-match-confirm-all-safe-btn"
            >
              {bulkBooking ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Zap className="h-3.5 w-3.5 mr-1.5" />
              )}
              {bulkBooking ? "Buche…" : `Alle sicheren Treffer buchen (${safeCount})`}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={reload}
            disabled={loading}
            data-testid="bank-match-refresh-btn"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Aktualisieren
          </Button>
        </div>
      </div>

      {/* FX-Diff-Toggle */}
      <div className="flex items-center justify-between gap-3 bg-amber-50/60 border border-amber-200 rounded-md px-3 py-2 mb-3">
        <div className="flex items-start gap-2 min-w-0">
          <ArrowRightLeft className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-amber-900">
            <span className="font-semibold">FX-Differenz automatisch buchen</span> — Wechselkurs-Diffs
            werden auf <code className="font-mono">6949</code> (Kursverlust) bzw. <code className="font-mono">6999</code> (Kursgewinn) gebucht.
          </div>
        </div>
        <Switch
          checked={autoBookFxDiff}
          onCheckedChange={setAutoBookFxDiff}
          data-testid="bank-match-fx-toggle"
        />
      </div>

      {/* Vorschläge-Liste */}
      {loading && suggestions.length === 0 ? (
        <div className="py-8 text-center text-sm text-slate-500" data-testid="bank-match-loading">
          <Loader2 className="h-5 w-5 mx-auto animate-spin mb-2" />
          Lade Vorschläge…
        </div>
      ) : suggestions.length === 0 ? (
        <div className="py-8 text-center text-sm text-slate-500" data-testid="bank-match-empty">
          <Check className="h-5 w-5 mx-auto text-emerald-500 mb-2" />
          Keine Match-Vorschläge — alle Bank-Buchungen sind zugeordnet oder es liegen keine
          passenden Belege vor.
        </div>
      ) : (
        <div className="space-y-2" data-testid="bank-match-list">
          {suggestions.map((s) => {
            const TypeIcon = CAND_TYPE[s.candidate_type]?.icon || Receipt;
            const isForeign = s.candidate_currency !== "CHF";
            return (
              <div
                key={s.journal_entry_id}
                className="border border-slate-200 rounded-md p-3 hover:border-violet-300 transition"
                data-testid={`bank-match-row-${s.journal_entry_id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  {/* Bank-Side */}
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 text-[11px] font-medium text-slate-500 uppercase tracking-wide">
                      <Banknote className="h-3 w-3" /> Bank
                    </div>
                    <div className="font-mono text-sm font-semibold text-slate-900 tabular-nums">
                      {s.bank_direction === "out" ? "−" : "+"} CHF {fmtCHF(s.bank_amount)}
                    </div>
                    <div className="text-xs text-slate-600 mt-0.5 flex flex-wrap items-center gap-x-1.5" data-testid={`bank-tx-dates-${s.journal_entry_id}`}>
                      <span>{s.bank_purchase_date ? "Belastet" : ""} {formatDate(s.bank_date)}</span>
                      {s.bank_purchase_date && (
                        <span className="text-violet-700 font-medium" data-testid={`bank-tx-purchase-${s.journal_entry_id}`}>
                          · Kauf {formatDate(s.bank_purchase_date)}
                        </span>
                      )}
                      {s.card_fee > 0 && (
                        <span className="text-slate-500">· Kartenzuschlag{s.bank_card_fee_pct ? ` ${s.bank_card_fee_pct} %` : ""} CHF {fmtCHF(s.card_fee)}</span>
                      )}
                      {s.bank_card_last4 && <span className="text-slate-500">· Karte {s.bank_card_last4}</span>}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5 break-words" data-testid={`bank-tx-details-${s.journal_entry_id}`}>
                      {s.bank_description}
                    </div>
                  </div>

                  <div className="text-slate-300 self-center">↔</div>

                  {/* Candidate-Side */}
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 text-[11px] font-medium text-slate-500 uppercase tracking-wide">
                      <TypeIcon className="h-3 w-3" />
                      {CAND_TYPE[s.candidate_type]?.label || s.candidate_type}
                    </div>
                    <div className="font-mono text-sm font-semibold text-slate-900 tabular-nums">
                      {isForeign ? (
                        <>
                          {s.candidate_currency} {fmtCHF(s.candidate_original_amount)}
                          <span className="text-[10px] text-slate-400 ml-1" data-testid={`bank-match-fx-${s.candidate_id}`}>
                            ({s.candidate_charged_chf ? "= " : "≈ "}CHF {fmtCHF(s.candidate_amount_chf)}{s.candidate_charged_chf ? " · lt. Zahlungsbeleg" : s.candidate_fx_rate_date ? ` · ESTV-Kurs ${formatDate(s.candidate_fx_rate_date)}` : ""})
                          </span>
                        </>
                      ) : (
                        <>CHF {fmtCHF(s.candidate_amount_chf)}</>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 break-words flex items-center gap-1.5">
                      <span>{formatDate(s.candidate_doc_date || s.candidate_date)} · {s.candidate_label}</span>
                      {(s.candidate_type === "receipt" || s.candidate_type === "bill") && (
                        <button
                          className="text-violet-600 hover:text-violet-800 inline-flex items-center shrink-0"
                          title={s.candidate_type === "bill" ? "Rechnung ansehen" : "Beleg ansehen"}
                          onClick={() => openReceiptPreview(s.candidate_id, s.candidate_label, s.candidate_type, s.candidate_attachments || [])}
                          data-testid={`bank-match-zoom-${s.candidate_id}`}
                        >
                          {previewLoading === s.candidate_id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <ZoomIn className="h-3.5 w-3.5" />
                          )}
                        </button>
                      )}
                    </div>
                    {s.candidate_type !== "invoice" && (
                      <div className="text-[11px] mt-0.5 inline-flex flex-wrap items-center gap-x-1.5" data-testid={`bank-match-dates-${s.candidate_id}`}>
                        {s.candidate_due_date && s.candidate_due_date !== (s.candidate_doc_date || s.candidate_date) && (
                          <span className="text-slate-500">Fällig {formatDate(s.candidate_due_date)} ·</span>
                        )}
                        {s.candidate_charged_chf && (
                          <span className="text-slate-700" data-testid={`bank-match-charged-${s.candidate_id}`}>
                            Belastet lt. Zahlungsbeleg CHF {fmtCHF(s.candidate_charged_chf)}{s.candidate_fx_rate ? ` (1 ${s.candidate_currency} = ${Number(s.candidate_fx_rate).toFixed(4)})` : ""} ·
                          </span>
                        )}
                        {s.candidate_paid_date ? (
                          <span className="text-emerald-700 inline-flex items-center gap-1" data-testid={`bank-match-paid-${s.candidate_id}`}>
                            <CreditCard className="h-3 w-3" /> Bezahlt/belastet am {formatDate(s.candidate_paid_date)}
                          </span>
                        ) : (
                          <span className="text-amber-700" title={s.bank_purchase_date ? "Der Beleg nennt kein Zahlungsdatum. Beim Bestätigen wird das Kaufdatum aus dem Banktext als Bezahldatum übernommen, das Bankdatum als Belastungsdatum." : "Der Beleg nennt kein Zahlungs-/Belastungsdatum. Beim Bestätigen wird das Bankdatum als Bezahldatum übernommen."} data-testid={`bank-match-paid-missing-${s.candidate_id}`}>
                            Bezahldatum nicht auf Beleg → bei Bestätigung {formatDate(s.bank_purchase_date || s.bank_date)}{s.bank_purchase_date ? ` (Kauf) · belastet ${formatDate(s.bank_date)}` : ""}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* FX-Diff Badge + Score */}
                  <div className="flex flex-col items-end gap-1 min-w-[120px]">
                    <ScorePill score={s.score} />
                    {Math.abs(s.fx_diff) >= 0.01 && (
                      <div
                        className={`inline-flex items-center gap-1 text-[11px] font-medium tabular-nums px-2 py-0.5 rounded ${
                          s.fx_diff > 0
                            ? "bg-rose-50 text-rose-700"
                            : "bg-emerald-50 text-emerald-700"
                        }`}
                        title={s.fx_diff > 0 ? "Bank zahlte mehr → Kursverlust" : "Bank zahlte weniger → Kursgewinn"}
                      >
                        {s.fx_diff > 0 ? (
                          <TrendingDown className="h-3 w-3" />
                        ) : (
                          <TrendingUp className="h-3 w-3" />
                        )}
                        FX-Diff {s.fx_diff > 0 ? "+" : ""}{fmtCHF(s.fx_diff)}
                      </div>
                    )}
                    {s.auto_bookable && mode === "auto_high" && (
                      <span className="text-[10px] text-violet-600 font-medium">Wird auto-gebucht</span>
                    )}
                  </div>
                </div>

                {/* Reasons + Action */}
                <div className="mt-2 flex items-center justify-between gap-2 flex-wrap pt-2 border-t border-slate-100">
                  <div className="flex items-center gap-1.5 text-[11px] text-slate-500 flex-wrap">
                    <Info className="h-3 w-3 text-slate-400" />
                    {(s.reasons || []).slice(0, 3).join(" · ")}
                    {(s.reasons || []).length > 3 && (
                      <span className="text-slate-400" title={(s.reasons || []).slice(3).join("\n")}> · +{(s.reasons || []).length - 3} weitere (Hover)</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                  {s.candidate_type === "receipt" && Math.abs(s.fx_diff || 0) > 0.009 && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2.5 text-xs border-amber-300 text-amber-800 hover:bg-amber-50"
                      onClick={() => recheckReceipt(s)}
                      disabled={recheckingId === s.candidate_id}
                      title="Beleg nochmals mit OCR + KI prüfen (z. B. falscher Total-Betrag erkannt)"
                      data-testid={`bank-match-recheck-${s.journal_entry_id}`}
                    >
                      {recheckingId === s.candidate_id ? <Loader2 className="h-3 w-3 animate-spin mr-1.5" /> : <RefreshCw className="h-3 w-3 mr-1.5" />}
                      Beleg neu prüfen
                    </Button>
                  )}
                  <Button
                    size="sm"
                    className="bg-violet-600 hover:bg-violet-700 text-white h-7 px-3 text-xs"
                    onClick={() => confirmMatch(s)}
                    disabled={confirmingId === s.journal_entry_id}
                    data-testid={`bank-match-confirm-${s.journal_entry_id}`}
                  >
                    {confirmingId === s.journal_entry_id ? (
                      <Loader2 className="h-3 w-3 animate-spin mr-1.5" />
                    ) : (
                      <Check className="h-3 w-3 mr-1.5" />
                    )}
                    Bestätigen
                  </Button>
                  </div>
                </div>

                {/* Iter257: Alternative Kandidaten bei unsicheren Matches */}
                {(s.alternatives || []).length > 0 && (
                  <div className="mt-2 pt-2 border-t border-dashed border-slate-200" data-testid={`bank-match-alternatives-${s.journal_entry_id}`}>
                    <div className="text-[11px] font-medium text-slate-400 uppercase tracking-wide mb-1.5">
                      Oder war es…?
                    </div>
                    <div className="space-y-1">
                      {s.alternatives.map((alt) => (
                        <div key={alt.candidate_id} className="flex items-center justify-between gap-2 bg-slate-50 rounded px-2 py-1.5" data-testid={`bank-match-alt-${alt.candidate_id}`}>
                          <div className="flex items-center gap-2 min-w-0 text-xs text-slate-600 flex-wrap">
                            <ScorePill score={alt.score} />
                            <span className="font-medium text-slate-800 tabular-nums shrink-0">{formatDate(alt.candidate_doc_date || alt.candidate_date)}</span>
                            {alt.date_distance != null && alt.date_distance < 99 && (
                              <span className={`text-[11px] shrink-0 ${alt.date_distance === 0 ? "text-emerald-700" : "text-slate-500"}`}>
                                {alt.date_distance === 0 ? "gleicher Tag" : `±${alt.date_distance} Tag${alt.date_distance > 1 ? "e" : ""}`}
                              </span>
                            )}
                            <span className="truncate">{alt.candidate_label}</span>
                            <span className="font-mono tabular-nums text-slate-500 shrink-0">
                              {alt.candidate_original_amount && (alt.candidate_original_currency || alt.candidate_currency) && (alt.candidate_original_currency || alt.candidate_currency) !== "CHF"
                                ? `${alt.candidate_original_currency || alt.candidate_currency} ${fmtCHF(alt.candidate_original_amount)} (≈ CHF ${fmtCHF(alt.candidate_amount_chf)})`
                                : `CHF ${fmtCHF(alt.candidate_amount_chf)}`}
                            </span>
                            {(alt.candidate_type === "receipt" || alt.candidate_type === "bill") && (
                              <button
                                className="text-violet-600 hover:text-violet-800 inline-flex items-center shrink-0"
                                title={alt.candidate_type === "bill" ? "Rechnung ansehen" : "Beleg ansehen"}
                                onClick={() => openReceiptPreview(alt.candidate_id, alt.candidate_label, alt.candidate_type, alt.candidate_attachments || [])}
                                data-testid={`bank-match-alt-zoom-${alt.candidate_id}`}
                              >
                                {previewLoading === alt.candidate_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ZoomIn className="h-3.5 w-3.5" />}
                              </button>
                            )}
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 px-2 text-[11px] shrink-0"
                            onClick={() => confirmMatch(s, alt)}
                            disabled={confirmingId === `${s.journal_entry_id}:${alt.candidate_id}`}
                            data-testid={`bank-match-alt-confirm-${alt.candidate_id}`}
                          >
                            {confirmingId === `${s.journal_entry_id}:${alt.candidate_id}` ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              "Diesen wählen"
                            )}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Iter258: Beleg-Vorschau (Lupe) */}
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o && preview) { URL.revokeObjectURL(preview.url); setPreview(null); } }}>
        <DialogContent className="max-w-3xl" data-testid="bank-match-receipt-preview">
          <DialogHeader>
            <DialogTitle className="text-sm">{preview?.label || "Beleg"}</DialogTitle>
          </DialogHeader>
          {preview?.attachments?.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="bank-match-preview-tabs">
              <button
                className={`rounded-full px-3 py-1 text-xs border ${!preview.activeId ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
                onClick={() => openReceiptPreview(preview.candidateId, preview.label, preview.kind, preview.attachments, null)}
                data-testid="bank-match-preview-tab-main"
              >
                {preview.kind === "bill" ? "Rechnung" : "Beleg"}
              </button>
              {preview.attachments.map((a) => (
                <button
                  key={a.file_id}
                  className={`rounded-full px-3 py-1 text-xs border ${preview.activeId === a.file_id ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
                  onClick={() => openReceiptPreview(preview.candidateId, preview.label, preview.kind, preview.attachments, a.file_id)}
                  data-testid={`bank-match-preview-tab-${a.file_id}`}
                >
                  {a.kind === "payment_receipt" || /receipt|quittung|zahlungsbeleg/i.test(a.file_name || "") ? "Zahlungsbeleg" : a.kind === "delivery_note" ? "Lieferschein" : a.kind === "reminder" ? "Mahnung" : "Beilage"} · {a.file_name}
                </button>
              ))}
            </div>
          )}
          {preview && (
            <div className="h-[70vh] rounded border border-slate-200 overflow-hidden flex bg-slate-100">
              <DocumentViewer blobUrl={preview.url} contentType={preview.mime} fileName={preview.label || "Beleg"} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
